import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, MapPin, Package, RefreshCw, CheckCircle, Clock, Truck, MessageCircle } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

const WHATSAPP_NUMBER = '5521971956796';

const PICKUP_COMPANY_ADDRESS = {
  name: 'PCS Dutos e Exaustores',
  line1: 'Estrada da Palhada, 3392',
  line2: 'Nova Iguaçu - Rio de Janeiro - RJ',
  line3: '',
};

const statusLabels = {
  pending: 'Pendente',
  confirmed: 'Confirmado',
  shipped: 'Enviado',
  delivered: 'Entregue',
  cancelled: 'Cancelado',
};

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

export const OrderDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');

  const isPickup = useMemo(() => order?.address?.delivery_method === 'pickup', [order]);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  const fetchOrder = async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/orders/${id}`, { withCredentials: true });
      setOrder(response.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Não foi possível carregar os detalhes do pedido');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!id) return;
    setSyncing(true);
    try {
      await axios.post(
        `${API}/mercadopago/sync-orders`,
        { order_ids: [id] },
        { withCredentials: true, timeout: 20000 }
      );
    } catch (e) {
      // não bloquear UX
    } finally {
      setSyncing(false);
      fetchOrder();
    }
  };

  const totalItems = useMemo(() => {
    if (!order?.items || !Array.isArray(order.items)) return 0;
    return order.items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
  }, [order]);

  const paymentLabel = useMemo(() => {
    if (order?.payment_status === 'paid') return 'Pago';
    if (order?.payment_status === 'failed') return 'Falhou';
    return 'Pendente';
  }, [order]);

  const timeline = useMemo(() => {
    const s = order?.status || 'pending';
    const map = {
      pending: 0,
      confirmed: 1,
      shipped: 2,
      delivered: 3,
      cancelled: -1,
    };
    const idx = map[s] ?? 0;

    return [
      { key: 'pending', label: 'Pedido recebido', icon: Clock, active: idx >= 0 && s !== 'cancelled' },
      { key: 'confirmed', label: 'Confirmado', icon: CheckCircle, active: idx >= 1 },
      { key: 'shipped', label: 'Em transporte', icon: Truck, active: idx >= 2 },
      { key: 'delivered', label: 'Entregue', icon: CheckCircle, active: idx >= 3 },
    ];
  }, [order]);

  const whatsappMessage = useMemo(() => {
    if (!order) return '';

    const orderId = String(order.id || '');
    const shortId = orderId ? orderId.slice(0, 8) : '';
    const orderCode = String(order.order_code || '');
    const createdAt = order.created_at ? new Date(order.created_at).toLocaleString('pt-BR') : '';

    const methodLabel = isPickup ? 'Retirada' : 'Entrega';
    const orderStatusLabel = statusLabels[order.status] || String(order.status || '');

    const addr = order.address || {};
    const addrLine1 = !isPickup
      ? `${addr.street || ''} ${addr.number || ''}${addr.complement ? `, ${addr.complement}` : ''}`.trim()
      : '';
    const addrLine2 = !isPickup
      ? `${addr.neighborhood || ''} - ${addr.city || ''}/${addr.state || ''}`.trim()
      : '';
    const addrLine3 = !isPickup ? `CEP: ${addr.zipcode || ''}`.trim() : '';

    const pickupLine1 = `${PICKUP_COMPANY_ADDRESS.name}`.trim();
    const pickupLine2 = `${PICKUP_COMPANY_ADDRESS.line1}`.trim();
    const pickupLine3 = `${PICKUP_COMPANY_ADDRESS.line2}`.trim();
    const pickupLine4 = `${PICKUP_COMPANY_ADDRESS.line3}`.trim();

    const itemsText = Array.isArray(order.items)
      ? order.items
          .map((it) => {
            const name = it.product_name || 'Item';
            const qty = Number(it.quantity || 0);
            const price = Number(it.price || 0);
            const lineTotal = (price * qty).toFixed(2);
            return `- ${name} (Qtd: ${qty}) - R$ ${lineTotal}`;
          })
          .join('\n')
      : '';

    const total = Number(order.total || 0).toFixed(2);

    return (
      `Olá! Vim pelo site da PCS Dutos e Exaustores.\n\n` +
      `Quero tirar dúvidas sobre meu pedido.\n\n` +
      `Pedido: #${shortId}${orderCode ? ` (${orderCode})` : ''}\n` +
      (createdAt ? `Data: ${createdAt}\n` : '') +
      `Cliente: ${user?.name || ''} (${user?.email || ''})\n` +
      `Status do pedido: ${orderStatusLabel}\n` +
      `Status do pagamento: ${paymentLabel}\n` +
      `Recebimento: ${methodLabel}\n` +
      (isPickup ? `Local de retirada:\n${pickupLine1}\n${pickupLine2}\n${pickupLine3}\n${pickupLine4}\n` : '') +
      (!isPickup && addrLine1 ? `Endereço: ${addrLine1}\n` : '') +
      (!isPickup && addrLine2 ? `${addrLine2}\n` : '') +
      (!isPickup && addrLine3 ? `${addrLine3}\n` : '') +
      `\nItens:\n${itemsText}\n\n` +
      `Total: R$ ${total}`
    );
  }, [order, isPickup, paymentLabel, user]);

  const handleWhatsApp = () => {
    const msg = whatsappMessage || '';
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] pt-24 pb-12">
        <div className="container mx-auto px-4">
          <Button variant="outline" onClick={() => navigate('/orders')} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-slate-700 dark:text-slate-200 font-semibold">{error}</p>
              <Button onClick={fetchOrder} className="mt-4">Tentar novamente</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] pt-24 pb-12">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <Button variant="outline" onClick={() => navigate('/orders')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>

          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleWhatsApp}>
              <MessageCircle className="h-4 w-4 mr-2" />
              WhatsApp do pedido
            </Button>
            <Button variant="outline" onClick={handleSync} disabled={syncing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              Atualizar status
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle>Pedido #{String(order.id).slice(0, 8)}</CardTitle>
                    {order.order_code && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Código: {order.order_code}</p>
                    )}
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                      {new Date(order.created_at).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <Badge className={statusColors[order.status] || statusColors.pending}>
                    {statusLabels[order.status] || 'Pendente'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400">
                  <span>Itens</span>
                  <span className="font-semibold text-slate-900 dark:text-white">{totalItems}</span>
                </div>
                <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400">
                  <span>Status do Pagamento</span>
                  <span className="font-semibold text-slate-900 dark:text-white">{paymentLabel}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-200 dark:border-slate-700">
                  <span>Total</span>
                  <span className="text-blue-600 dark:text-blue-400">R$ {Number(order.total || 0).toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Produtos
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(order.items || []).map((item, idx) => (
                  <div key={idx} className="flex justify-between items-start gap-4 border-b border-slate-200 dark:border-slate-700 pb-3 last:border-b-0 last:pb-0">
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">{item.product_name}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Quantidade: {item.quantity}</p>
                    </div>
                    <p className="font-semibold text-blue-600 dark:text-blue-400">
                      R$ {(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  {isPickup ? 'Retirada' : 'Entrega'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {isPickup ? (
                  <div className="text-slate-700 dark:text-slate-200">
                    <p className="font-semibold">Retirada na loja</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                      Após a confirmação do pagamento, entre em contato clicando no botão do whatsapp acima, para combinarmos a retirada.
                    </p>
                    <div className="mt-4 space-y-1">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">Endereço</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{PICKUP_COMPANY_ADDRESS.name}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{PICKUP_COMPANY_ADDRESS.line1}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{PICKUP_COMPANY_ADDRESS.line2}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{PICKUP_COMPANY_ADDRESS.line3}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-slate-700 dark:text-slate-200">
                    <p className="font-semibold">Endereço de entrega</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                      {order.address?.street} {order.address?.number}{order.address?.complement ? `, ${order.address?.complement}` : ''}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {order.address?.neighborhood} - {order.address?.city}/{order.address?.state}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">CEP: {order.address?.zipcode}</p>
                    {order.address?.phone && (
                      <p className="text-sm text-slate-600 dark:text-slate-400">Telefone: {order.address?.phone}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Acompanhamento</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {order.status === 'cancelled' ? (
                  <div className="text-slate-700 dark:text-slate-200">
                    <p className="font-semibold">Pedido cancelado</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Se precisar de ajuda, fale conosco.</p>
                  </div>
                ) : isPickup ? (
                  <div className="text-slate-700 dark:text-slate-200">
                    <p className="font-semibold">Retirada</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                      Assim que o pagamento estiver <span className="font-semibold">Pago</span>, o pedido será liberado para retirada.
                    </p>
                    <div className="mt-4 space-y-1">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">Local de retirada</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{PICKUP_COMPANY_ADDRESS.name}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{PICKUP_COMPANY_ADDRESS.line1}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{PICKUP_COMPANY_ADDRESS.line2}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{PICKUP_COMPANY_ADDRESS.line3}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {timeline.map((t) => {
                      const Icon = t.icon;
                      return (
                        <div key={t.key} className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${t.active ? 'bg-blue-600' : 'bg-slate-200 dark:bg-slate-700'}`}>
                            <Icon className={`h-5 w-5 ${t.active ? 'text-white' : 'text-slate-500 dark:text-slate-300'}`} />
                          </div>
                          <div>
                            <p className={`font-semibold ${t.active ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-300'}`}>
                              {t.label}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Ajuda</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Se tiver dúvidas sobre seu pedido, entre em contato pelo WhatsApp.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
